import {
  getOptionFlowRowSentiment,
  getOptionFlowSideCode,
  OptionFlowRowSentiment,
} from './getOptionFlowRowSentiment';
import { normalizeCallPut } from './normalizeCallPut';
import { CallPut, OrderType, Trade, TradeDirection } from './types';
import { calculatePremiumScore } from './scores';

export type OptionFlowMoneynessBucket = 'deep_itm' | 'itm' | 'atm' | 'otm' | 'deep_otm' | 'unknown';

export type OptionFlowSideReliability = 'high' | 'medium' | 'low' | 'unknown';

export type OptionFlowSideIntelligenceTag =
  | 'POSITION_EXIT'
  | 'LOTTERY'
  | 'CONVICTION'
  | 'STOCK_REPLACEMENT'
  | 'HEDGE'
  | 'PREMIUM_SELL'
  | 'INCOME'
  | 'SWEEP'
  | 'WIDE_SPREAD';

export type OptionFlowSideIntelligence = {
  sideCode: TradeDirection | '';
  sentiment: OptionFlowRowSentiment;
  moneynessBucket: OptionFlowMoneynessBucket;
  reliability: OptionFlowSideReliability;
  tags: OptionFlowSideIntelligenceTag[];
  summaryLabel: string;
  tooltip: string;
};

export type OptionFlowReadTag = {
  label: OptionFlowReadLabel | OptionFlowDirectionalOptionType;
  tooltip: string;
  kind: 'primary' | 'modifier';
};

type SideIntelligenceTrade = Partial<
  Pick<
    Trade,
    | 'cp'
    | 'tradeDirection'
    | 'tradeSide'
    | 'otmPercent'
    | 'strike'
    | 'spot'
    | 'nbboAsk'
    | 'nbboBid'
    | 'premium'
    | 'size'
    | 'openInterest'
    | 'avgPremiumPerTrade'
    | 'orderType'
  >
>;

const SIDE_CODE_LABELS: Record<TradeDirection, string> = {
  [TradeDirection.AboveAsk]: 'Above Ask',
  [TradeDirection.Ask]: 'Ask',
  [TradeDirection.Mid]: 'Midpoint',
  [TradeDirection.Bid]: 'Bid',
  [TradeDirection.BelowBid]: 'Below Bid',
};

const MONEYNESS_LABELS: Record<OptionFlowMoneynessBucket, string> = {
  deep_itm: 'Deep ITM',
  itm: 'ITM',
  atm: 'ATM',
  otm: 'OTM',
  deep_otm: 'Deep OTM',
  unknown: 'Unknown moneyness',
};

const RELIABILITY_LABELS: Record<OptionFlowSideReliability, string> = {
  high: 'High',
  medium: 'Medium',
  low: 'Low',
  unknown: 'Unknown',
};

const TAG_LABELS: Record<OptionFlowSideIntelligenceTag, string> = {
  POSITION_EXIT: 'Likely Close',
  LOTTERY: 'Lottery',
  CONVICTION: 'Conviction',
  STOCK_REPLACEMENT: 'Stock Replacement',
  HEDGE: 'Hedge',
  PREMIUM_SELL: 'Premium Sell',
  INCOME: 'Income',
  SWEEP: 'Sweep',
  WIDE_SPREAD: 'Wide Spread',
};

export const OPTION_FLOW_READ_LABELS = [
  'Neutral',
  'Exit',
  'Lottery',
  'Hedge',
  'Income',
  'Stock Proxy',
  'Big Bet',
] as const;

export const OPTION_FLOW_DIRECTIONAL_OPTION_TYPES = [
  'Bull Calls',
  'Bear Calls',
  'Bull Puts',
  'Bear Puts',
  'Bull Trades',
  'Bear Trades',
] as const;

export type OptionFlowReadLabel = (typeof OPTION_FLOW_READ_LABELS)[number];
export type OptionFlowDirectionalOptionType = (typeof OPTION_FLOW_DIRECTIONAL_OPTION_TYPES)[number];

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const getSignedOtmPercent = (trade?: SideIntelligenceTrade | null): number | null => {
  if (isFiniteNumber(trade?.otmPercent)) {
    return trade.otmPercent;
  }

  const cp = normalizeCallPut(trade?.cp);

  if (!cp || !isFiniteNumber(trade?.strike) || !isFiniteNumber(trade?.spot) || trade.spot <= 0) {
    return null;
  }

  if (cp === CallPut.Call) {
    return ((trade.strike - trade.spot) / trade.spot) * 100;
  }

  return ((trade.spot - trade.strike) / trade.spot) * 100;
};

export const getOptionFlowMoneynessBucket = (
  trade?: SideIntelligenceTrade | null,
): OptionFlowMoneynessBucket => {
  const otmPercent = getSignedOtmPercent(trade);

  if (!isFiniteNumber(otmPercent)) {
    return 'unknown';
  }

  if (otmPercent <= -20) {
    return 'deep_itm';
  }

  if (otmPercent < -2) {
    return 'itm';
  }

  if (otmPercent <= 2) {
    return 'atm';
  }

  if (otmPercent < 16) {
    return 'otm';
  }

  return 'deep_otm';
};

export const getOptionFlowSideReliability = (
  trade?: SideIntelligenceTrade | null,
): OptionFlowSideReliability => {
  const bid = trade?.nbboBid;
  const ask = trade?.nbboAsk;

  if (!isFiniteNumber(bid) || !isFiniteNumber(ask) || bid <= 0 || ask <= 0 || ask < bid) {
    return 'unknown';
  }

  const midpoint = (ask + bid) / 2;

  if (midpoint <= 0) {
    return 'unknown';
  }

  const spreadPercent = ((ask - bid) / midpoint) * 100;

  if (spreadPercent <= 5) {
    return 'high';
  }

  if (spreadPercent <= 15) {
    return 'medium';
  }

  return 'low';
};

const isAskSide = (sideCode: TradeDirection | '') =>
  sideCode === TradeDirection.Ask || sideCode === TradeDirection.AboveAsk;

const isBidSide = (sideCode: TradeDirection | '') =>
  sideCode === TradeDirection.Bid || sideCode === TradeDirection.BelowBid;

const hasLargePremium = (trade?: SideIntelligenceTrade | null) => {
  if (!trade) {
    return false;
  }

  return (trade.premium ?? 0) >= 50_000 || calculatePremiumScore(trade as Trade) >= 2;
};

const isLikelyClosingPosition = (
  trade: SideIntelligenceTrade | null | undefined,
  cp: CallPut | null,
  sideCode: TradeDirection | '',
  moneynessBucket: OptionFlowMoneynessBucket,
  reliability: OptionFlowSideReliability,
) => {
  if (!trade || !cp || !isBidSide(sideCode) || reliability === 'low' || reliability === 'unknown') {
    return false;
  }

  const size = trade.size || 0;
  const openInterest = trade.openInterest || 0;

  if (size <= 0 || openInterest <= 0 || openInterest < size) {
    return false;
  }

  const isItmCloseCandidate = moneynessBucket === 'itm' || moneynessBucket === 'deep_itm';
  const isAtmCloseCandidate = moneynessBucket === 'atm';
  const hasLargeNotional =
    (trade.premium || 0) >= 100_000 || size >= 500 || calculatePremiumScore(trade as Trade) >= 2;

  if (isItmCloseCandidate) {
    return hasLargeNotional;
  }

  return (
    isAtmCloseCandidate &&
    openInterest >= size * 1.5 &&
    size >= 500 &&
    ((trade.premium || 0) >= 200_000 || calculatePremiumScore(trade as Trade) >= 3)
  );
};

const addTag = (tags: OptionFlowSideIntelligenceTag[], tag: OptionFlowSideIntelligenceTag) => {
  if (!tags.includes(tag)) {
    tags.push(tag);
  }
};

const getPrimarySetupLabel = (
  moneynessBucket: OptionFlowMoneynessBucket,
  tags: OptionFlowSideIntelligenceTag[],
  sentiment: OptionFlowRowSentiment,
  cp: CallPut | null,
) => {
  if (moneynessBucket === 'unknown') {
    return `${sentiment} ${
      cp === CallPut.Put ? 'put' : cp === CallPut.Call ? 'call' : 'option'
    } side`;
  }

  const moneynessLabel = MONEYNESS_LABELS[moneynessBucket];

  if (tags.includes('LOTTERY')) {
    return `${moneynessLabel} Lottery`;
  }

  if (tags.includes('POSITION_EXIT')) {
    return 'Likely Position Close';
  }

  if (tags.includes('HEDGE')) {
    return `${moneynessLabel} Hedge`;
  }

  if (tags.includes('PREMIUM_SELL')) {
    return `${moneynessLabel} Premium-Sell Income`;
  }

  if (tags.includes('STOCK_REPLACEMENT')) {
    return `${moneynessLabel} Stock Replacement`;
  }

  if (tags.includes('CONVICTION')) {
    return `${moneynessLabel} Conviction`;
  }

  return `${moneynessLabel} Side Read`;
};

export const getOptionFlowMoneynessBucketLabel = (bucket: OptionFlowMoneynessBucket) =>
  MONEYNESS_LABELS[bucket];

export const getOptionFlowSideReliabilityLabel = (reliability: OptionFlowSideReliability) =>
  RELIABILITY_LABELS[reliability];

export const getOptionFlowSideIntelligenceTagLabel = (tag: OptionFlowSideIntelligenceTag) =>
  TAG_LABELS[tag];

const getDirectionalOptionTypeLabel = (
  sentiment: OptionFlowRowSentiment,
  cp: CallPut | null,
): OptionFlowDirectionalOptionType | undefined => {
  if (sentiment === OptionFlowRowSentiment.Neutral) {
    return undefined;
  }

  const direction = sentiment === OptionFlowRowSentiment.Bullish ? 'Bull' : 'Bear';
  const optionType = cp === CallPut.Put ? 'Puts' : cp === CallPut.Call ? 'Calls' : 'Trades';

  return `${direction} ${optionType}` as OptionFlowDirectionalOptionType;
};

export const getOptionFlowDirectionalOptionType = (
  trade?: SideIntelligenceTrade | null,
): OptionFlowDirectionalOptionType | undefined => {
  const sideIntelligence = getOptionFlowSideIntelligence(trade);
  const cp = normalizeCallPut(trade?.cp);

  return getDirectionalOptionTypeLabel(sideIntelligence.sentiment, cp);
};

export const getOptionFlowSideIntelligence = (
  trade?: SideIntelligenceTrade | null,
): OptionFlowSideIntelligence => {
  const sideCode = getOptionFlowSideCode(trade);
  const sentiment = getOptionFlowRowSentiment(trade);
  const cp = normalizeCallPut(trade?.cp);
  const moneynessBucket = getOptionFlowMoneynessBucket(trade);
  const reliability = getOptionFlowSideReliability(trade);
  const tags: OptionFlowSideIntelligenceTag[] = [];
  const isSweep = trade?.orderType === OrderType.Sweep;

  if (isSweep) {
    addTag(tags, 'SWEEP');
  }

  if (reliability === 'low') {
    addTag(tags, 'WIDE_SPREAD');
  }

  if (!sideCode || sideCode === TradeDirection.Mid || !cp) {
    const tooltip = sideCode
      ? `${sideCode} - Midpoint print. No directional side read.`
      : 'No side code available.';

    return {
      sideCode,
      sentiment,
      moneynessBucket,
      reliability,
      tags,
      summaryLabel: 'Midpoint print - no directional side read',
      tooltip,
    };
  }

  const canUseMoneynessTags = moneynessBucket !== 'unknown';
  const canUseConviction = reliability !== 'low' && hasLargePremium(trade);

  if (canUseMoneynessTags) {
    const isOtm = moneynessBucket === 'otm' || moneynessBucket === 'deep_otm';
    const isAtm = moneynessBucket === 'atm';
    const isItm = moneynessBucket === 'itm' || moneynessBucket === 'deep_itm';

    if (isLikelyClosingPosition(trade, cp, sideCode, moneynessBucket, reliability)) {
      addTag(tags, 'POSITION_EXIT');
    } else if (cp === CallPut.Call) {
      if (isAskSide(sideCode) && isOtm) {
        addTag(tags, 'LOTTERY');
        if (canUseConviction) {
          addTag(tags, 'CONVICTION');
        }
      } else if (isAskSide(sideCode) && isAtm && canUseConviction) {
        addTag(tags, 'CONVICTION');
      } else if (isAskSide(sideCode) && isItm) {
        addTag(tags, 'STOCK_REPLACEMENT');
      } else if (isBidSide(sideCode) && isOtm) {
        addTag(tags, 'PREMIUM_SELL');
        addTag(tags, 'INCOME');
      }
    } else if (cp === CallPut.Put) {
      if (isAskSide(sideCode) && isOtm) {
        addTag(tags, 'HEDGE');
        if (canUseConviction) {
          addTag(tags, 'CONVICTION');
        }
      } else if (isAskSide(sideCode) && isAtm && reliability !== 'low') {
        addTag(tags, 'CONVICTION');
      } else if (isAskSide(sideCode) && isItm) {
        addTag(tags, 'STOCK_REPLACEMENT');
      } else if (isBidSide(sideCode) && isOtm) {
        addTag(tags, 'PREMIUM_SELL');
        addTag(tags, 'INCOME');
      }
    }
  }

  const summaryLabel = getPrimarySetupLabel(moneynessBucket, tags, sentiment, cp);
  const cpLabel = cp === CallPut.Put ? 'put' : 'call';
  const reliabilityLabel = RELIABILITY_LABELS[reliability];
  const sideLabel = sideCode ? SIDE_CODE_LABELS[sideCode] : 'Unknown side';
  const wideSpreadText = reliability === 'low' ? '; wide spread' : '';

  return {
    sideCode,
    sentiment,
    moneynessBucket,
    reliability,
    tags,
    summaryLabel,
    tooltip: `${
      sideCode || '-'
    } - ${sentiment} ${cpLabel} side (${sideLabel}). ${summaryLabel}. ${reliabilityLabel} side reliability${wideSpreadText}.`,
  };
};

export const getOptionFlowReadTags = (
  trade?: SideIntelligenceTrade | null,
): OptionFlowReadTag[] => {
  const sideIntelligence = getOptionFlowSideIntelligence(trade);
  const tags = sideIntelligence.tags;

  if (!sideIntelligence.sideCode || sideIntelligence.sideCode === TradeDirection.Mid) {
    return [
      {
        label: 'Neutral',
        tooltip: 'Midpoint print. No reliable directional side read.',
        kind: 'primary',
      },
    ];
  }

  if (tags.includes('POSITION_EXIT')) {
    return [
      {
        label: 'Exit',
        tooltip: 'Likely closing position from a large bid-side sale with enough open interest.',
        kind: 'primary',
      },
    ];
  }

  if (tags.includes('LOTTERY')) {
    return [
      {
        label: 'Lottery',
        tooltip: 'Out-of-the-money option buying with speculative upside or protection read.',
        kind: 'primary',
      },
    ];
  }

  if (tags.includes('HEDGE')) {
    return [
      {
        label: 'Hedge',
        tooltip: 'Put buying read as downside protection or bearish hedge demand.',
        kind: 'primary',
      },
    ];
  }

  if (tags.includes('PREMIUM_SELL') || tags.includes('INCOME')) {
    return [
      {
        label: 'Income',
        tooltip: 'Bid-side out-of-the-money option selling; premium income read.',
        kind: 'primary',
      },
    ];
  }

  if (tags.includes('STOCK_REPLACEMENT')) {
    return [
      {
        label: 'Stock Proxy',
        tooltip: 'In-the-money option used like stock-replacement exposure.',
        kind: 'primary',
      },
    ];
  }

  if (tags.includes('CONVICTION')) {
    return [
      {
        label: 'Big Bet',
        tooltip: 'Large premium makes this read stand out.',
        kind: 'primary',
      },
    ];
  }

  const directionalOptionType = getOptionFlowDirectionalOptionType(trade);

  if (!directionalOptionType) {
    return [];
  }

  return [
    {
      label: directionalOptionType,
      tooltip: `${directionalOptionType} read from contract type and execution side.`,
      kind: 'primary',
    },
  ];
};

export const getOptionFlowReadLabel = (
  trade?: SideIntelligenceTrade | null,
): OptionFlowReadTag['label'] | undefined => getOptionFlowReadTags(trade)[0]?.label;
